<template>
  <div class="termsOfUse">
    <v-row>
      <v-col
        class="p-0 pl-3"
        cols="6"
      >
        <VSelect
          v-model="selectedLang"
          :label="$t('forms.labels.lang')"
          itemValue="key"
          :items="displayedLangs"
          outlined
        />
      </v-col>
    </v-row>

    <v-row
      v-for="lang in teamLangs"
      v-show="selectedLang === lang"
      :key="lang"
    >
      <span class="termsOfUseTitle">{{ $t('administration.termsOfUse') }}</span>
      <GlobalComment
        position="opening"
        :comment="{ message: description[lang] }"
        :showAddSentences="false"
        :canUploadImages="false"
        :isLab="true"
        :showSaveBtn="false"
        editorDataCy="termsOfUse"
        @commentChange="description[lang] = $event"
      />
    </v-row>

    <v-row>
      <VCheckbox
        v-model="displayInNextLoginTerms"
        outlined
        :label="$t('advancedConfiguration.displayInNextLogin')"
      />
    </v-row>

    <v-row justify="end">
      <v-btn
        color="secondary"
        :disabled="!canSave"
        @click="onSave"
      >
        {{ $t('generic.save') }}
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import GlobalComment from '@/views/laboratory/workOrder/summary/GlobalComment';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox';
import { mapGetters } from 'vuex';

export default {
    name: 'TermsOfUse',
    components: {
        VSelect,
        GlobalComment,
        VCheckbox,
    },
    data() {
        return {
            description: {},
            originalDescription: {},
            selectedLang: '',
            selectedPlatform: 'web',
            displayInNextLoginTerms: false,
            originalDisplayInNextLoginTerms: false,
        };
    },
    computed: {
        ...mapGetters(['teamLangs']),
        displayedLangs() {
            return this.teamLangs.map(lang => ({
                name: this.$t(`langs.${lang}`),
                key: lang,
            }));
        },
        canSave() {
            const descChanged = this.teamLangs.some(
                lang => (this.description[lang]?.trim() || '') !== (this.originalDescription[lang]?.trim() || ''),
            );

            const allFilled = this.teamLangs.every(
                lang => (this.description[lang]?.trim() || '') !== '',
            );

            const displayChanged = this.displayInNextLoginTerms !== this.originalDisplayInNextLoginTerms;

            return allFilled && (descChanged || displayChanged);
        },
    },
    created() {
        this.initializeDescriptions();
        this.getData();
    },
    methods: {
        initializeDescriptions() {
            this.teamLangs.forEach(lang => {
                this.$set(this.description, lang, '');
                this.$set(this.originalDescription, lang, '');
            });
            const [firstLang] = this.teamLangs;
            this.selectedLang = firstLang;
        },
        async getData() {
            const { data } = await this.$rq.getTermsOfUse(this.selectedPlatform);
            const latest = Array.isArray(data) && data.length ? data[0] : null;

            if (latest?.description) {
                this.teamLangs.forEach(lang => {
                    this.$set(this.description, lang, latest.description[lang] || '');
                    this.$set(this.originalDescription, lang, latest.description[lang] || '');
                });
            }

            if (latest?.displayInNextLoginTerms !== undefined) {
                this.displayInNextLoginTerms = latest.displayInNextLoginTerms;
                this.originalDisplayInNextLoginTerms = latest.displayInNextLoginTerms;
            }
        },
        async onSave() {
            const payload = [
                {
                    platform: this.selectedPlatform,
                    description: Object.fromEntries(
                        this.teamLangs.map(lang => [lang, this.description[lang]?.trim() || '']),
                    ),
                    displayInNextLoginTerms: this.displayInNextLoginTerms,
                    active: true,
                },
            ];

            await this.$rq.updateTermsOfUse(payload);
            this.$toasted.global.toast_success({
                message: this.$t('graphics.series.msgUpdateSuccess'),
            });
            await this.getData();
        },
    },
};
</script>

<style lang="scss" scoped>
.termsOfUse {
  padding: 1rem 3rem 0 3rem;
  &Title {
    font-size: 1.5rem;
    font-weight: 500;
  }
}
</style>
